import { LazoConversationReadStatus } from "@/enums/LazoConversationReadStatus";
import { LazoPaths } from "@/enums/LazoPaths";
import { LazoThreadConversationStatus } from "@/enums/LazoThreadConversationStatus";
import { IPageV2 } from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/core/IPageV2";
import NotificationService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/NotificationService";
import { AVAILABLE_CONVERSATION_FILTERS, LazoChatConversationShell, mapToShell } from "@/models/ai-chat/LazoChatConversation";
import { LazoChatMessage } from "@/models/ai-chat/LazoChatMessage";
import { SmallConversationMember } from "@/models/ai-chat/SmallConversationMember";
import LazoChatConversationService from "@/services/ai-chat/LazoChatConversationService";
import LazoChatMessageService from "@/services/ai-chat/LazoChatMessageService";
import { ExternalId } from "@/types/ExternalId";
import { Dispatch, SetStateAction, createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { generatePath, useNavigate } from "react-router-dom";
import { PageFetchFn } from "../one/OneChatContext";
import { CHANNELS } from "./PortalChatContainer";

type PortalChatContext = {
  chats: LazoChatConversationShell[];
  setChats: Dispatch<SetStateAction<LazoChatConversationShell[]>>;
  privateChat: boolean;
  setPrivateChat: Dispatch<SetStateAction<boolean>>;
  groupChat: boolean;
  setGroupChat: Dispatch<SetStateAction<boolean>>;
  members: SmallConversationMember[];
  setMembers: Dispatch<SetStateAction<SmallConversationMember[]>>;
  messages: LazoChatMessage[];
  setMessages: Dispatch<SetStateAction<LazoChatMessage[]>>;
  step: number;
  setStep: Dispatch<SetStateAction<number>>;
  selectedChat: LazoChatConversationShell | null;
  setSelectedChat: Dispatch<SetStateAction<LazoChatConversationShell | null>>;
  loadingMessages: boolean;
  loadingMembers: boolean;
  loadingPage: boolean;
  firstLoad: boolean;
  firstLoadMessages: boolean;
  fetchMoreMessages: PageFetchFn;
  userExternalIdToTenantNamesMap: UserExternalIdTenantNames;
  filters: PortalFilters;
  setFilters: Dispatch<SetStateAction<PortalFilters>>;
  internalChat: LazoChatConversationShell | null;
  setInternalChat: Dispatch<SetStateAction<LazoChatConversationShell | null>>;
  generalChat: LazoChatConversationShell | null;
  setGeneralChat: Dispatch<SetStateAction<LazoChatConversationShell | null>>;
  chatChannel: CHANNELS;
  setChatChannel: Dispatch<SetStateAction<CHANNELS>>;
  handleChatSelect: (chat: LazoChatConversationShell) => void;
};

export const PortalChatListFetchMoreEvent = "portalChatListFetchMoreEvent";

const LazoPortalChatContext = createContext<PortalChatContext | undefined>(undefined);

export enum GeneralPortalFilters {
  ALL = 1,
  MENTIONS = 2,
  SHARED_WITH_ME = 3,
  TEAM = 4,
  URGENT = 5,
  READ_ONLY = 6,
}

export type UserExternalIdTenantNames = { [k: ExternalId]: string };
export type PageState = {
  page: number;
  totalPages: number | null;
};

export type PortalFilters = {
  clientsExternalIds: ExternalId[];
  employeesExternalIds: ExternalId[];
  tenantExternalIds: ExternalId[];
  searchValue: string;
  otherClients: boolean;
  generalPortalFilters: GeneralPortalFilters;
  showResponseTimes: boolean;
  status: LazoThreadConversationStatus;
  readStatus: LazoConversationReadStatus;
};

export const EMPTY_FILTERS: PortalFilters = {
  clientsExternalIds: [],
  employeesExternalIds: [],
  tenantExternalIds: [],
  otherClients: false,
  searchValue: null,
  generalPortalFilters: GeneralPortalFilters.ALL,
  showResponseTimes: false,
  status: null,
  readStatus: null,
};

export function mapToNewListWithUpdatedTarget(
  chats: LazoChatConversationShell[],
  target: ExternalId,
  update: Partial<LazoChatConversationShell>
): LazoChatConversationShell[] {
  return chats.map((chat) => {
    if (chat.externalId === target) {
      return { ...chat, ...update };
    }
    return chat;
  });
}

export function LazoPortalChatProvider({ children }) {
  const [chats, setChats] = useState<LazoChatConversationShell[]>([]);
  const [privateChat, setPrivateChat] = useState<boolean>(false);
  const [groupChat, setGroupChat] = useState<boolean>(false);

  const [members, setMembers] = useState<SmallConversationMember[]>([]);

  const [messages, setMessages] = useState<LazoChatMessage[]>([]);
  const [step, setStep] = useState<number>(0);
  const [selectedChat, setSelectedChat] = useState<LazoChatConversationShell | null>(null);

  const [generalChat, setGeneralChat] = useState<LazoChatConversationShell>(null);
  const [internalChat, setInternalChat] = useState<LazoChatConversationShell>(null);
  const [chatChannel, setChatChannel] = useState<CHANNELS>(CHANNELS.REPLY);

  const [loadingMessages, setLoadingMessages] = useState<boolean>(false);
  const [loadingMembers, setLoadingMembers] = useState<boolean>(false);

  const [firstLoad, setFirstLoad] = useState<boolean>(false);
  const [loadingPage, setLoadingPage] = useState<boolean>(false);

  const [chatsPageState, setPageState] = useState<PageState>({ page: -1, totalPages: null });

  const [firstLoadMessages, setFirstLoadMessages] = useState<boolean>(true);
  const [messagesPage, setMessagesPage] = useState<PageState>({ page: -1, totalPages: null });

  const [filters, setFilters] = useState<PortalFilters>({ ...EMPTY_FILTERS });

  const navigate = useNavigate();

  function updateConversations(
    data: IPageV2<LazoChatConversationShell>,
    currentData: LazoChatConversationShell[]
  ): LazoChatConversationShell[] {
    if (data.page.number === 0) {
      setPageState({ page: data.page.number, totalPages: data.page.totalPages });
      return data.content;
    } else {
      setPageState((prev) => ({ ...prev, page: data.page.number }));
      return currentData.concat(data.content);
    }
  }

  function hasMorePages(pageToFetch: number, state: PageState): boolean {
    return state.totalPages === null || state.totalPages === undefined || pageToFetch < state.totalPages;
  }

  const onFilterChats = useCallback(
    (filter: AVAILABLE_CONVERSATION_FILTERS, resetPage?: PageState) => {
      const pageToFetch = resetPage ? resetPage.page : chatsPageState.page + 1;
      if (!hasMorePages(pageToFetch, resetPage || chatsPageState)) return;

      resetPage && setFirstLoad(true);
      !resetPage && setLoadingPage(true);

      LazoChatConversationService.getChatsByFilter(filter, filters, pageToFetch)
        .then((res) => {
          res
            .onSuccess((data) => setChats(updateConversations(data, chats)))
            .onError((error) => NotificationService.error("Error getting inbox chats: " + error));
        })
        .finally(() => {
          setLoadingPage(false);
          setFirstLoad(false);
        });
    },
    [chatsPageState, filters]
  );

  function getInboxChatsByFilter(_?: any, resetPage?: PageState) {
    switch (filters.generalPortalFilters) {
      case GeneralPortalFilters.ALL:
        onFilterChats(AVAILABLE_CONVERSATION_FILTERS.ALL, resetPage);
        break;
      case GeneralPortalFilters.MENTIONS:
        onFilterChats(AVAILABLE_CONVERSATION_FILTERS.MENTIONS, resetPage);
        break;
      case GeneralPortalFilters.SHARED_WITH_ME:
        break;
      case GeneralPortalFilters.TEAM:
        onFilterChats(AVAILABLE_CONVERSATION_FILTERS.TEAM, resetPage);
        break;
      case GeneralPortalFilters.URGENT:
        break;
      case GeneralPortalFilters.READ_ONLY:
        onFilterChats(AVAILABLE_CONVERSATION_FILTERS.ADMIN_READ_ONLY, resetPage);
        break;
    }
  }

  // Setup event structure
  useEffect(() => {
    document.removeEventListener(PortalChatListFetchMoreEvent, getInboxChatsByFilter);
    document.addEventListener(PortalChatListFetchMoreEvent, getInboxChatsByFilter);
    return () => {
      document.removeEventListener(PortalChatListFetchMoreEvent, getInboxChatsByFilter);
    };
  }, [chatsPageState.page]);

  // Reset paging on filter change
  useEffect(() => {
    setPageState({ page: -1, totalPages: null });
    getInboxChatsByFilter(null, { page: 0, totalPages: null });
  }, [filters]);

  function readMessages(chat: LazoChatConversationShell) {
    if (chat.externalId) {
      LazoChatConversationService.readConversation(chat.externalId).then((_) => {
        setChats((prev) =>
          prev.map((c) => {
            if (c.externalId === chat.externalId) {
              return { ...c, unreadMessagesCount: 0 };
            }
            return c;
          })
        );
      });
    }
  }

  function handleChatSelect(chat: LazoChatConversationShell) {
    setSelectedChat(chat);
    if (chat === null) {
      return;
    }

    navigate(generatePath(LazoPaths.PORTAL_INBOX, { LazoChatTeamExternalId: chat.externalId }), { replace: true });

    // TODO: Is this needed?
    if (!chat.readOnly) {
      readMessages(chat);
    }
  }

  function fetchMembers(externalId: string) {
    setLoadingMembers(true);
    LazoChatConversationService.getChatMembers(externalId)
      .then((r) => {
        r.onSuccess((c) => {
          setMembers(c);
        });
        r.notifyErrorIfPresent();
      })
      .finally(() => setLoadingMembers(false));
  }

  const fetchMessages = useCallback(
    (resetPage?: PageState) => {
      if (!selectedChat) return;

      const pageToFetch = resetPage ? resetPage.page : messagesPage.page + 1;
      if (!hasMorePages(pageToFetch, resetPage || messagesPage)) return;

      resetPage && setFirstLoadMessages(true);
      !resetPage && setLoadingMessages(true);

      LazoChatMessageService.findByConversationExternalIdPaged(selectedChat.externalId, pageToFetch)
        .then((r) => {
          r.onSuccess((data) => {
            if (data.page.number === 0) {
              setMessagesPage({ page: data.page.number, totalPages: data.page.totalPages });
              setMessages(data.content);
            } else {
              setMessagesPage((prev) => ({ ...prev, page: data.page.number }));
              setMessages([...data.content, ...messages]);
            }
          });
          r.notifyErrorIfPresent();
        })
        .finally(() => {
          setLoadingMessages(false);
          setFirstLoadMessages(false);
        });
    },
    [messagesPage, selectedChat?.externalId]
  );

  useEffect(() => {
    if (!selectedChat || !selectedChat.externalId) {
      return;
    }

    setMessagesPage({ page: -1, totalPages: null });
    fetchMessages({ page: 0, totalPages: null });
    fetchMembers(selectedChat.externalId);
  }, [selectedChat?.externalId]);

  const userExternalIdToTenantNamesMap: UserExternalIdTenantNames = useMemo(() => {
    const m = {};
    for (let i = 0; i < members.length; i++) {
      const cm = members[i];
      m[cm.userExternalId] = cm.tenantNames;
    }
    return m;
  }, [members]);

  function getInternalConversation(internalChannelExternalId: ExternalId) {
    if (!internalChannelExternalId) return;
    LazoChatConversationService.getInternalChat(internalChannelExternalId).then((r) => {
      r.onSuccess((conv) => {
        setInternalChat(mapToShell(conv));
      });
      r.notifyErrorIfPresent();
    });
  }

  useEffect(() => {
    if (!selectedChat) {
      return;
    }

    getInternalConversation(selectedChat.internalChannelExternalId);
    if (selectedChat.externalId !== internalChat?.externalId) {
      setGeneralChat(selectedChat);
      setChatChannel(CHANNELS.REPLY);
    }
  }, [selectedChat]);

  return (
    <LazoPortalChatContext.Provider
      value={{
        chats,
        setChats,
        privateChat,
        setPrivateChat,
        groupChat,
        setGroupChat,
        members,
        setMembers,
        messages,
        setMessages,
        step,
        setStep,
        selectedChat,
        setSelectedChat,
        loadingMessages,
        loadingMembers,
        loadingPage,
        firstLoad,
        firstLoadMessages,
        fetchMoreMessages: fetchMessages,
        userExternalIdToTenantNamesMap,
        filters,
        setFilters,
        internalChat,
        setInternalChat,
        generalChat,
        setGeneralChat,
        chatChannel,
        setChatChannel,
        handleChatSelect,
      }}
    >
      {children}
    </LazoPortalChatContext.Provider>
  );
}

export const usePortalChatContext = (): PortalChatContext => {
  const context = useContext(LazoPortalChatContext);
  return context;
};
